import { ChangeEvent, useState } from "react";
import { flushSync } from "react-dom";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { SxProps, Theme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { formatCpf, isValidCpf, isValidEmail, isValidFullName, nextMaskedValue } from "./masks";

export type SmartFieldKind = "cpf" | "crm" | "email" | "name";

type Kind = {
  label: string;
  placeholder: string;
  inputMode: "numeric" | "email" | "text";
  type?: string;
  masked: boolean;
  format: (v: string) => string;
  max?: number;
  help: string;
  isValid: (v: string) => boolean;
  error: (v: string) => string;
  progress?: (v: string) => string;
  normalize?: (v: string) => string;
  autoComplete: string;
};

const missing = (n: number) => `falta${n > 1 ? "m" : ""} ${n} número${n > 1 ? "s" : ""}`;

const KINDS: Record<SmartFieldKind, Kind> = {
  cpf: {
    label: "N° CPF", placeholder: "000.000.000-00", inputMode: "numeric", masked: true, format: formatCpf, max: 11,
    help: "Só números; a pontuação entra sozinha", isValid: isValidCpf,
    error: (v) => (v.length < 11 ? `CPF incompleto — ${missing(11 - v.length)}` : "CPF inválido — confira os números"),
    progress: (v) => (v.length ? ` · ${v.length}/11` : ""), autoComplete: "off",
  },
  crm: {
    label: "CRM", placeholder: "Ex.: 123456", inputMode: "numeric", masked: true, format: (v) => v,
    help: "Só os números, sem a sigla do estado", isValid: (v) => v !== "", error: () => "Informe o número do CRM", autoComplete: "off",
  },
  email: {
    label: "Email", placeholder: "nome@exemplo.com.br", inputMode: "email", type: "email", masked: false, format: (v) => v,
    help: "Para onde enviaremos o contrato para assinatura", isValid: isValidEmail,
    error: () => "E-mail inválido — confira (ex.: nome@exemplo.com.br)", normalize: (v) => v.trim().toLowerCase(), autoComplete: "email",
  },
  name: {
    label: "Nome Completo", placeholder: "Como está no seu documento", inputMode: "text", masked: false, format: (v) => v,
    help: "Nome e sobrenome", isValid: isValidFullName, error: () => "Digite nome e sobrenome", autoComplete: "name",
  },
};

export type SmartFieldProps = { kind: SmartFieldKind; value: string; onValue: (value: string) => void; showErrors?: boolean; sx?: SxProps<Theme> };

export default function SmartField({ kind, value, onValue, showErrors = false, sx }: SmartFieldProps) {
  const k = KINDS[kind];
  const [focused, setFocused] = useState(false);
  const [left, setLeft] = useState(false);
  const v = value ?? "";
  // Fora do foco valida o valor já normalizado: e-mail com maiúsculas não pisca erro no blur (G1.12).
  const valid = k.isValid(!focused && k.normalize ? k.normalize(v) : v);
  const showError = !focused && (left || showErrors) && !valid;
  const showOk = valid && (!focused || kind === "cpf");
  const helper = showError ? k.error(v) : showOk ? (kind === "cpf" ? "CPF válido" : " ") : `${k.help}${k.progress && focused ? k.progress(v) : ""}`;

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!k.masked) {
      onValue(e.target.value);
      return;
    }
    const el = e.target;
    const inputType = (e.nativeEvent as InputEvent | undefined)?.inputType;
    const next = nextMaskedValue({ raw: el.value, caret: el.selectionStart, prevDigits: v, format: k.format, max: k.max, inputType });
    onValue(next.digits);
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => {
        try {
          el.setSelectionRange(next.caret, next.caret);
        } catch {
          /* campo sem foco */
        }
      });
    }
  };

  return (
    <TextField
      sx={sx}
      fullWidth
      label={k.label}
      type={k.type || "text"}
      placeholder={k.placeholder}
      value={k.masked ? k.format(v) : v}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        // Aplica já o estado de "saiu do campo": o blur não é um evento com restauração de input controlado,
        // então o React 18 adiaria a atualização para uma microtask e o erro apareceria um tique depois.
        flushSync(() => {
          setFocused(false);
          setLeft(true);
        });
        if (k.normalize && v) onValue(k.normalize(v));
      }}
      error={showError}
      color={showOk ? "success" : undefined}
      helperText={helper}
      FormHelperTextProps={{ sx: { color: showOk && kind === "cpf" ? "#1e6b34" : undefined } }}
      InputLabelProps={{ shrink: true }}
      inputProps={{ inputMode: k.inputMode, autoComplete: k.autoComplete, "aria-invalid": showError || undefined }}
      InputProps={{
        endAdornment: showOk ? (
          <InputAdornment position="end"><CheckCircleIcon sx={{ color: "#2e7d32" }} aria-hidden /></InputAdornment>
        ) : showError ? (
          <InputAdornment position="end"><ErrorIcon color="error" aria-hidden /></InputAdornment>
        ) : undefined,
      }}
    />
  );
}
